import React from "react";
import {
    Archive24Regular,
    ArrowForward20Regular,
    ArrowReply20Regular,
    ArrowReply24Regular,
    ArrowReplyAll20Regular,
    MoreHorizontal24Regular,
    PersonAdd24Regular,
    TaskListSquareLtr24Regular,
} from "@fluentui/react-icons";
import BottomSheet from "../../calendar/BottomSheet";
import "./mailMobil.css";

export interface KopfAktion {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
}

export interface MailKopfAktionenProps {
    onAntworten: () => void;
    onAllenAntworten: () => void;
    onWeiterleiten: () => void;
    /** Fehlt ohne Archivrecht am Postfach. */
    onArchivieren?: () => void;
    /** Einträge hinter „Vorgang" (bzw. „Schaden" im Schadenpostfach). */
    vorgang: KopfAktion[];
    vorgangLabel: string;
    onKontaktZuweisen?: () => void;
    /** „Mehr": Gruppen, getrennt durch eine Linie. */
    weitere: KopfAktion[][];
}

/**
 * Aktionsreihe einer geöffneten Mail auf dem Handy: EINE Zeile gleich großer
 * Icon-Knöpfe — Antworten ▾, Archivieren, Vorgang, Kontakt, Mehr. Was dahinter
 * zur Wahl steht, öffnet ein Sheet; nichts bricht um, nichts wird abgeschnitten.
 */
export const MailKopfAktionen: React.FC<MailKopfAktionenProps> = ({
    onAntworten,
    onAllenAntworten,
    onWeiterleiten,
    onArchivieren,
    vorgang,
    vorgangLabel,
    onKontaktZuweisen,
    weitere,
}) => {
    const [sheet, setSheet] = React.useState<"antworten" | "vorgang" | "mehr" | null>(null);
    const zeile = (a: KopfAktion) => (
        <button
            key={a.label}
            type="button"
            className="mm-sheet-zeile"
            disabled={a.disabled}
            onClick={() => {
                setSheet(null);
                a.onClick();
            }}
        >
            {a.icon}
            <span className="mm-label">{a.label}</span>
        </button>
    );
    return (
        <>
            <div className="mm-aktionen" role="toolbar" aria-label="Aktionen für diese Mail">
                <button type="button" className="mm-leiste-btn" onClick={() => setSheet("antworten")} aria-haspopup="dialog">
                    <ArrowReply24Regular />
                    <span>Antworten ▾</span>
                </button>
                {onArchivieren && (
                    <button type="button" className="mm-leiste-btn" onClick={onArchivieren}>
                        <Archive24Regular />
                        <span>Archivieren</span>
                    </button>
                )}
                {vorgang.length > 0 && (
                    <button type="button" className="mm-leiste-btn" onClick={() => setSheet("vorgang")} aria-haspopup="dialog">
                        <TaskListSquareLtr24Regular />
                        <span>{vorgangLabel}</span>
                    </button>
                )}
                {onKontaktZuweisen && (
                    <button type="button" className="mm-leiste-btn" onClick={onKontaktZuweisen}>
                        <PersonAdd24Regular />
                        <span>Kontakt</span>
                    </button>
                )}
                <button
                    type="button"
                    className="mm-leiste-btn"
                    aria-label="Weitere Aktionen"
                    aria-haspopup="dialog"
                    onClick={() => setSheet("mehr")}
                >
                    <MoreHorizontal24Regular />
                    <span>Mehr</span>
                </button>
            </div>
            <div className="cal-rd">
                <BottomSheet open={sheet === "antworten"} title="Antworten" onClose={() => setSheet(null)}>
                    {zeile({ label: "Antworten", icon: <ArrowReply20Regular />, onClick: onAntworten })}
                    {zeile({ label: "Allen antworten", icon: <ArrowReplyAll20Regular />, onClick: onAllenAntworten })}
                    {zeile({ label: "Weiterleiten", icon: <ArrowForward20Regular />, onClick: onWeiterleiten })}
                </BottomSheet>
                <BottomSheet open={sheet === "vorgang"} title={vorgangLabel} onClose={() => setSheet(null)}>
                    {vorgang.map(zeile)}
                </BottomSheet>
                <BottomSheet open={sheet === "mehr"} title="Weitere Aktionen" onClose={() => setSheet(null)}>
                    {weitere
                        .filter((g) => g.length > 0)
                        .map((gruppe, i) => (
                            <div
                                key={i}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    ...(i > 0 ? { borderTop: "1px solid #ece7f0", paddingTop: 8 } : {}),
                                }}
                            >
                                {gruppe.map(zeile)}
                            </div>
                        ))}
                </BottomSheet>
            </div>
        </>
    );
};
